/**
 * CustomerBillingPage — `/dashboard/customer/billing`.
 *
 * Phase 2 — Decision #8 LIVE: relocate the credit-history experience
 * out of `CreditHistoryDrawer` and into a dedicated route page.
 * Layout mirrors the drawer body but inline + scrollable in the main
 * shell area. Reuses `/credits/me` (the same endpoint the drawer
 * called) for symmetry.
 */
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";

function formatTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function CustomerBillingPage() {
  const { profile } = useAuth();
  const { creditLabel } = useConfig();
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const balance = Number(profile?.credit_balance || 0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/credits/me")
      .then(({ data }) => {
        if (cancelled) return;
        setLedger(data.ledger || []);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="customer-billing-page">
      <header className="mb-8">
        <span className="text-sm font-bold uppercase tracking-wider text-brand-orange">
          Customer dashboard
        </span>
        <h1 className="font-heading text-4xl font-bold text-pp-text sm:text-5xl">
          Billing
        </h1>
        <p className="mt-2 text-muted-foreground">
          Your {creditLabel} balance and ledger history.
        </p>
      </header>

      <Card
        className="mb-8 rounded-3xl border-border bg-pp-surface2 p-6"
        data-testid="customer-billing-balance-card"
        style={{ boxShadow: "var(--pp-card-shadow)" }}
      >
        <p className="text-xs font-bold uppercase tracking-wider text-pp-text-muted">
          {creditLabel.toUpperCase()}
        </p>
        <p
          className="mt-1 font-heading text-5xl font-bold text-brand-orange"
          data-testid="customer-billing-balance"
        >
          ${balance.toFixed(2)}
        </p>
        <p className="mt-2 text-xs text-muted-foreground">
          Applied automatically to future bookings (coming soon).
        </p>
      </Card>

      <Card className="rounded-3xl border-border bg-pp-surface p-6 shadow-none" data-testid="customer-billing-ledger">
        <h2 className="mb-4 font-heading text-xl font-semibold text-pp-text">
          History
        </h2>
        {loading ? (
          <div className="flex items-center gap-2 py-8 text-muted-foreground" data-testid="customer-billing-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading {creditLabel} history…
          </div>
        ) : ledger.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="customer-billing-empty">
            No {creditLabel} activity yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customer-billing-ledger-table">
              <thead>
                <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wider text-pp-text-muted">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Reason</th>
                  <th className="py-2 pr-4 text-right">Amount</th>
                  <th className="py-2 pr-4 text-right">Balance after</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((row) => (
                  <tr key={row.id} className="border-b border-border last:border-b-0">
                    <td className="py-2 pr-4 text-muted-foreground">{formatTs(row.created_at)}</td>
                    <td className="py-2 pr-4 font-medium text-pp-text">{row.entry_type}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{row.reason || "—"}</td>
                    <td className={`py-2 pr-4 text-right font-mono tabular-nums font-semibold ${
                      row.delta >= 0 ? "text-emerald-600" : "text-red-600"
                    }`}>
                      {row.delta >= 0 ? "+" : ""}${Number(row.delta).toFixed(2)}
                    </td>
                    <td className="py-2 pr-4 text-right font-mono tabular-nums text-pp-text">
                      ${Number(row.balance_after || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
